import { BlockBlobClient } from '@azure/storage-blob';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { firstValueFrom, Observable, of, switchMap } from 'rxjs';

export interface IBlobStorageTokenService {
  getBlobStorageToken(params: { fileName?: string }): Observable<{
    createToken: string;
    readToken: string;
  }>;
}
export const BLOB_STORAGE_TOKEN_SERVICE = new InjectionToken<IBlobStorageTokenService>('Service for getting storage token');

export interface IBlockListUploader {
  stageBlock(block: Blob): void;
  commit(fileName: string, contentType: string): void;
}

class BlockListUploader implements IBlockListUploader {
  private _blockIds: string[] = [];

  constructor(
    private blockBlobClient: BlockBlobClient,
    private readToken: string,
    private contentType: string,
  ) {}

  get blobUrl() {
    return this.readToken;
  }

  async stageBlock(block: Blob) {
    let blockId = `MZ_${new Date().getTime()}_${this._blockIds.length}`;
    blockId = btoa(blockId.padEnd(64, '0'));
    this._blockIds.push(blockId);
    await this.blockBlobClient.stageBlock(blockId, block, block.size);
  }

  async commit() {
    this.blockBlobClient.commitBlockList(this._blockIds, {
      blobHTTPHeaders: {
        blobContentType: this.contentType,
      },
    });
    this._blockIds = [];
  }
}

@Injectable()
export class BlobStorageService {
  service = inject(BLOB_STORAGE_TOKEN_SERVICE);

  uploadFile(file: Blob, fileName: string, contentType = 'application/octet-stream') {
    return this.service
      .getBlobStorageToken({
        fileName,
      })
      .pipe(
        switchMap(({ createToken, readToken }) =>
          new BlockBlobClient(createToken)
            .upload(file, file.size, {
              blobHTTPHeaders: {
                blobContentType: contentType,
              },
            })
            .then(() => readToken),
        ),
      );
  }

  beginUpload(fileName: string, contentType = 'application/octet-stream') {
    return firstValueFrom(
      this.service
        .getBlobStorageToken({
          fileName,
        })
        .pipe(
          switchMap(({ createToken, readToken }) => of(new BlockListUploader(new BlockBlobClient(createToken), readToken, contentType))),
        ),
    );
  }
}
